import type { ReactNode } from 'react';
import type { Feature } from 'geojson';

import { GeoJSONGeometry, GeoJSONPoint, LocationGeometry } from './geojson';
import { RoleType } from './user';
import { WorkPriorityType } from './work';

export type MapStyle = 'default' | 'silver' | 'retro' | 'dark';
export type MapTypeId = 'roadmap' | 'satellite' | 'hybrid' | 'terrain';

export type AtomMapUiPreferences = {
  mapStyle: MapStyle;
  mapTypeId: MapTypeId;
  traffic: boolean;
  poi: boolean;
};

export type MapCamera = {
  center: { lat: number; lng: number };
  zoom: number;
};

export type MapIdleEvent = MapCamera & {
  coordinates: string | null;
};

export type MapSearch = {
  searchPoint?: { lat: number; lng: number };
  searchTerm?: string;
};

export type ViewState = MapCamera & MapSearch;

export type EditTarget = {
  id: string;
  name?: string;
  /** The kind of entity being placed. */
  type: 'asset' | 'user' | 'workOrder';
  /** Marker shown in read mode (inactive state). */
  icon: ReactNode;
  /** Marker shown while editing (active state). Falls back to `icon`. */
  activeIcon?: ReactNode;
  /** Icon shown in the search-box edit list. Falls back to `icon`. */
  listIcon?: ReactNode;
  /** Address line shown beneath the name in the edit list for assets. */
  address?: string;
  /** Current geometry, null if unset. */
  location: GeoJSONGeometry | null;
  /** Stroke color for a LineString target's static path. */
  color?: string;
  canEdit: boolean;
};

export type LineCoordinates = [[number, number], [number, number]];
export type LineStringCoordinates = Array<[number, number]>;

export type LocationUpdatePayload = {
  location: LocationGeometry;
};

export type LocationLinkOption = {
  text: string;
  onClick: () => void;
};

export type MapCluster = {
  id: string;
  location: GeoJSONPoint;
  count: number;
};

export type MapWorkOrderAssignee = {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
  email: string;
};

export type MapWorkOrder = {
  id: string;
  name: string;
  inventoryAssetId: string;
  inventoryAssetName: string;
  type: string;
  statusId: number | string;
  priorityId: WorkPriorityType;
  location: GeoJSONGeometry;
  estimatedCost: number;
  dueDate: number;
  plannedStartDate?: number;
  taskCount: number;
  assigneeCount: number;
  assignees?: MapWorkOrderAssignee[];
};

export type MapAsset = {
  id: string;
  name: string;
  assetType: string;
  updatedDate: number;
  markerId: number;
  location: GeoJSONGeometry;
  mainPhotoFileId?: string;
  schemaId?: string;
};

export type MapLocation = {
  id: string;
  name?: string;
  geometry: GeoJSONGeometry;
  statusId: number | string;
  isActive?: boolean;
};

export type MapUser = {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
  role: RoleType;
  location: GeoJSONPoint;
  email: string;
};

export type InventoryMapFilters = {
  schemaId: string;
  categoryIds: string;
  hasPendingChangesOnly: boolean;
  assetFilters: { [key: string]: any[] };
};

export type MapData = {
  clusters?: MapCluster[];
  assets?: MapAsset[];
  assetsCount?: number;
  workOrders?: MapWorkOrder[];
  workOrdersCount?: number;
  users?: MapUser[];
  usersCount?: number;
  locations?: MapLocation[];
};

export type MapAssetsFilterInput = {
  coordinates: number[][][];
  zoomLevel: number;
  clusterDensity?: string;
  schemaId?: string;
  categoryIds?: string[];
  hasPendingChangesOnly?: boolean;
  attributeFilters?: object;
};

export type MapInput = {
  coordinates: number[][][];
  zoomLevel: number;
  clusterDensity?: string;
  includeWorkOrders?: boolean;
  includeAssets?: boolean;
  includeUsers?: boolean;
  dueDateStart?: number;
  dueDateEnd?: number;
  plannedStartDateStart?: number;
  plannedStartDateEnd?: number;
  workCreatedDateStart?: number;
  workCreatedDateEnd?: number;
  statusIds?: string[];
  userIds?: string[];
  priorityIds?: number[];
  workTemplateIds?: string[];
  workCreatedByIds?: string[];
  workTemplateFolderIds?: string[];
  schemaIds?: string[];
  categoryIds?: string[];
  showAdmin?: boolean;
};

/** `[r, g, b, a]`, each 0–255 — the convention deck.gl color accessors use. */
export type RgbaColor = [number, number, number, number];

/** A point marker image (e.g. an ArcGIS picture-marker symbol) drawn at each point. */
export type SymbolIcon = {
  url: string;
  width: number;
  height: number;
  /**
   * Anchor point in the icon's own pixel space, measured from the top-left. The
   * anchor is placed on the feature's coordinate. Omit for a centered anchor
   * (`width / 2`, `height / 2`) — e.g. a KML pushpin anchors at its bottom tip.
   */
  anchorX?: number;
  anchorY?: number;
};

/**
 * Per-collection styling in deck.gl form. Each color accessor is a constant
 * RGBA tuple or a per-feature function (for source-driven renderers). When
 * `icon` is set, points render as that image; omitted fields use the defaults.
 */
export type CollectionSymbology = {
  getFillColor?: RgbaColor | ((feature: Feature) => RgbaColor);
  getLineColor?: RgbaColor | ((feature: Feature) => RgbaColor);
  /** Per-feature line/outline width in pixels (e.g. a KML `<LineStyle><width>`). */
  getLineWidth?: number | ((feature: Feature) => number);
  icon?: SymbolIcon;
  /**
   * Per-feature point icon (e.g. KML placemarks carrying their own image).
   * Takes precedence over the constant `icon`; return `null` to fall back to a
   * default colored circle.
   */
  getIcon?: (feature: Feature) => SymbolIcon | null;
};

export enum KmlClientType {
  WEB = 'WEB',
  MOBILE = 'MOBILE',
  BOTH = 'BOTH',
}

export type KmlExtendedData = {
  type: 'TITLE' | 'SUBTITLE' | 'MARKER_ID' | 'ATTRIBUTE';
  key: string;
  name: string;
};

export type KmlLayer = {
  id: string;
  name: string;
  markerId: number;
  url: string;
  pollingInterval?: number;
  suppressInfoWindows: boolean;
  extendedData: KmlExtendedData[];
  client: KmlClientType;
};

export enum BoundingBoxSubjectType {
  LOCATION = 'LOCATION',
  ASSET = 'ASSET',
  ALL = 'ALL',
}

export type BoundingBox = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
};

export type TaskBoundingBoxInput = {
  workOrderId: string;
  taskId: string;
  subjectType?: BoundingBoxSubjectType;
};
